import { firebaseService } from './firebase.js';
import { userService } from './user.js';
import { validateAdAccountIds } from '../utils/validators.js';
import { orderBy } from 'firebase/firestore';

class ClientService {
  constructor() {
    this.clients = [];
    this.listeners = [];
    this.activeSubscription = null;
    this.isInitialized = false; // Track if initial load is complete
  }

  // ==================== INITIALIZATION ====================

  async initialize() {
    try {
      if (userService.isAgency()) {
        const agency = userService.getCurrentAgency();
        if (agency?.id) {
          await this.loadClients();
          this.setupRealtimeSubscription();
        }
        // Skip initialization silently if agency not ready
      }
    } catch (error) {
      // Silently handle - client service will retry on demand
    }
  }

  async loadClients() {
    const agency = userService.getCurrentAgency();
    if (!userService.isAgency() || !agency?.id) {
      this.clients = [];
      this.isInitialized = true;
      return;
    }

    try {
      this.clients = await firebaseService.getAgencyClients(agency.id);
      this.isInitialized = true;
      this.notifyListeners();
    } catch (error) {
      // Silently handle - will show empty client list
      this.clients = [];
      this.isInitialized = true;
    }
  }

  setupRealtimeSubscription() {
    if (this.activeSubscription) {
      firebaseService.unsubscribe(this.activeSubscription);
    }

    const agency = userService.getCurrentAgency();
    if (!userService.isAgency() || !agency?.id) {
      return;
    }

    const collectionPath = `agencies/${agency.id}/clients`;
    
    this.activeSubscription = firebaseService.subscribeToCollection(
      collectionPath,
      (clients) => {
        this.clients = clients;
        this.isInitialized = true;
        this.notifyListeners();
      },
      [orderBy('createdAt', 'desc')]
    );
  }

  // ==================== CLIENT CRUD OPERATIONS ====================

  async addClient(clientData) {
    if (!userService.isAgency()) {
      throw new Error('Apenas agências podem adicionar clientes');
    }

    const agency = userService.getCurrentAgency();
    const agencyId = agency?.id;
    if (!agencyId) {
      throw new Error('Agência não encontrada. Por favor, faça login novamente.');
    }

    try {
      // Check client limit
      const maxClients = userService.getMaxClients();
      if (this.clients.length >= maxClients) {
        throw new Error(`Limite de ${maxClients} clientes atingido. Faça upgrade do seu plano.`);
      }

      const client = await firebaseService.createClient(agencyId, {
        ...clientData,
        status: 'active',
        createdBy: userService.currentUser?.uid
      });

      // Atualizar array local imediatamente para evitar race condition
      // O Firestore subscription também atualizará, mas pode haver delay
      if (client && !this.clients.find(c => c.id === client.id)) {
        this.clients.unshift(client); // Adicionar no início (mais recente)
        this.notifyListeners();
      }

      return client;
    } catch (error) {
      throw error;
    }
  }

  async updateClient(id, updates) {
    if (!userService.isAgency()) {
      throw new Error('Apenas agências podem atualizar clientes');
    }

    const agencyId = userService.getCurrentAgency()?.id;
    if (!agencyId) {
      throw new Error('Agência não encontrada');
    }

    try {
      // Gerencia automaticamente a data de saída baseado no status
      const currentClient = this.getClient(id);
      const newStatus = updates.status;
      const oldStatus = currentClient?.status;

      // Se status mudou para inativo ou suspenso, define endDate automaticamente
      if (newStatus && newStatus !== oldStatus) {
        if ((newStatus === 'inactive' || newStatus === 'suspended') && oldStatus === 'active') {
          // Cliente saiu - define data de saída se não existir
          if (!updates.endDate) {
            updates.endDate = new Date().toISOString().split('T')[0];
          }
        } else if (newStatus === 'active' && (oldStatus === 'inactive' || oldStatus === 'suspended')) {
          // Cliente voltou - limpa data de saída
          updates.endDate = null;
        }
      }

      const updatedClient = await firebaseService.updateClient(agencyId, id, updates);

      // Atualizar array local imediatamente para refletir mudancas
      const index = this.clients.findIndex(c => c.id === id);
      if (index !== -1 && updatedClient) {
        this.clients[index] = { ...this.clients[index], ...updatedClient };
        this.notifyListeners();
      }

      return updatedClient;
    } catch (error) {
      throw error;
    }
  }

  async deleteClient(id) {
    if (!userService.isAgency()) {
      throw new Error('Apenas agências podem excluir clientes');
    }

    const agencyId = userService.getCurrentAgency()?.id;
    if (!agencyId) {
      throw new Error('Agência não encontrada');
    }

    try {
      await firebaseService.deleteClient(agencyId, id);

      // Remover do array local imediatamente para evitar cliente fantasma na UI
      const index = this.clients.findIndex(c => c.id === id);
      if (index !== -1) {
        this.clients.splice(index, 1);
        this.notifyListeners();
      }

      return true;
    } catch (error) {
      throw error;
    }
  }

  // ==================== CLIENT GETTERS ====================

  getClient(id) {
    return this.clients.find(client => client.id === id);
  }

  getAllClients() {
    return [...this.clients];
  }
  
  getClientCount() {
    return this.clients.length;
  }

  getActiveClients() {
    return this.clients.filter(client => client.status === 'active');
  }

  async getClientById(id) {
    if (!userService.canAccessClientData(userService.getCurrentAgency()?.id, id)) {
      throw new Error('Acesso negado ao cliente');
    }

    const agencyId = userService.getCurrentAgency()?.id;
    if (!agencyId) {
      throw new Error('Agência não encontrada');
    }

    try {
      return await firebaseService.getClient(agencyId, id);
    } catch (error) {
      throw error;
    }
  }

  searchClients(query) {
    const lowercaseQuery = query.toLowerCase();
    return this.clients.filter(client =>
      client.name.toLowerCase().includes(lowercaseQuery) ||
      client.email.toLowerCase().includes(lowercaseQuery) ||
      client.company.toLowerCase().includes(lowercaseQuery)
    );
  }

  getClientsByStatus(status) {
    return this.clients.filter(client => client.status === status);
  }

  /**
   * Retorna o ID da agência atual
   * @returns {string|null} ID da agência ou null se não disponível
   */
  getAgencyId() {
    return userService.getCurrentAgency()?.id || null;
  }

  // ==================== EVENT HANDLING ====================

  onClientsChanged(callback) {
    this.listeners.push(callback);
    callback(this.clients);
  }

  notifyListeners() {
    this.listeners.forEach(callback => callback(this.clients));
    
    // Notify dashboard to update metrics
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('clientsUpdated'));
    }
  }

  // ==================== VALIDATION ====================

  validateClient(clientData) {
    const errors = [];

    if (!clientData.name || clientData.name.trim() === '') {
      errors.push('Nome é obrigatório');
    }

    // Email é opcional, mas se preenchido deve ser válido
    if (clientData.email && clientData.email.trim() !== '') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)) {
        errors.push('Email inválido');
      }

      // Verificar se já existe um cliente com este email
      const existingClient = this.clients.find(client =>
        client.email === clientData.email && client.id !== clientData.id
      );
      if (existingClient) {
        errors.push('Já existe um cliente com este email');
      }
    }

    // Validate Meta Ad Account IDs
    if (clientData.metaAdAccountIds && clientData.metaAdAccountIds.length > 0) {
      const metaValidation = validateAdAccountIds(clientData.metaAdAccountIds, 'meta');
      if (metaValidation.errors.length > 0) {
        errors.push(...metaValidation.errors);
      }
    }

    // Validate Google Ad Account IDs
    if (clientData.googleAdAccountIds && clientData.googleAdAccountIds.length > 0) {
      const googleValidation = validateAdAccountIds(clientData.googleAdAccountIds, 'google');
      if (googleValidation.errors.length > 0) {
        errors.push(...googleValidation.errors);
      }
    }

    return errors;
  }

  // ==================== CLEANUP ====================

  cleanup() {
    if (this.activeSubscription) {
      firebaseService.unsubscribe(this.activeSubscription);
      this.activeSubscription = null;
    }
    
    this.clients = [];
    this.listeners = [];
  }

  // Method to reinitialize when agency becomes available
  async reinitialize() {
    this.cleanup();
    await this.initialize();
  }
}

export const clientService = new ClientService();